import React, { useCallback, useState } from "react"

import {
  Alert,
  Button,
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Label,
} from "@coval/ui"

import {
  getCurrentDeployment,
  getDevIdError,
  LOCAL_PIPELINE_SUMMARY,
  LOCAL_PIPELINE_TITLE,
} from "@/lib/local-worker-launch"

interface LocalWorkerDialogProps {
  onClose: () => void
  devId: string
  onDevIdChange: (devId: string) => void
}

/**
 * Edits the shared dev_id and states what setting it does.
 *
 * Holds its own draft so typing is not fought by the trim the store applies on save.
 * Mounted only while open, so the draft starts from the stored value every time and an
 * abandoned one cannot come back.
 */
export const LocalWorkerDialog: React.FC<LocalWorkerDialogProps> = ({
  onClose,
  devId,
  onDevIdChange,
}) => {
  const [draftDevId, setDraftDevId] = useState(devId)
  const deployment = getCurrentDeployment()
  const devIdError = getDevIdError(draftDevId)

  const handleSave = useCallback(() => {
    if (getDevIdError(draftDevId)) {
      return
    }
    onDevIdChange(draftDevId)
    onClose()
  }, [draftDevId, onDevIdChange, onClose])

  const handleClear = useCallback(() => {
    onDevIdChange("")
    onClose()
  }, [onDevIdChange, onClose])

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent size="md" data-testid="local-worker-dialog">
        <DialogHeader>
          <DialogTitle>{LOCAL_PIPELINE_TITLE}</DialogTitle>
        </DialogHeader>
        <DialogBody className="flex flex-col gap-4 py-4">
          <Alert variant="info">{LOCAL_PIPELINE_SUMMARY}</Alert>
          <p className="text-sm text-coval-text">
            Your local machine must be running the{" "}
            <span className="font-medium">{deployment}</span> environment.
          </p>
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="local-worker-dev-id">COVAL_DEV_ID</Label>
            <Input
              id="local-worker-dev-id"
              aria-label="Developer ID"
              value={draftDevId}
              onChange={(event) => setDraftDevId(event.target.value)}
              className="font-mono"
              onKeyDown={(event) => {
                if (event.key === "Enter") {
                  handleSave()
                }
              }}
            />
            <p
              className={
                devIdError
                  ? "text-xs text-coval-danger-text"
                  : "text-xs text-coval-text-muted"
              }
            >
              {devIdError ??
                `Clear it to run everything on the ${deployment} fleet.`}
            </p>
          </div>
        </DialogBody>
        <DialogFooter className="gap-2 px-4 pb-4">
          <Button variant="secondary" onClick={handleClear} disabled={!devId}>
            Clear
          </Button>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={Boolean(devIdError)}>
            Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
